import { useOnline } from "@vueuse/core";
import { defineStore } from "pinia";
import { io, type Socket } from "socket.io-client";
import { computed, ref } from "vue";

import { useCommentsStore } from "@/stores/comments.ts";
import { useConfigStore } from "@/stores/config.ts";
import { useTopicStore } from "@/stores/topics.ts";
import { getAccessToken } from "@/utils/apiClients.ts";

export const useMessengerSocketStore = defineStore(
  "messengerSocketStore",
  () => {
    const configStore = useConfigStore();
    const commentsStore = useCommentsStore();
    const topicStore = useTopicStore();
    const isConnected = ref<boolean>(false);
    const isTopicSocketDataUpdated = ref(false);
    const isCommentSocketDataUpdated = ref(false);
    const isSocketAuthenticated = ref(false);
    const socket = ref<Socket>();
    const token = ref<string | undefined>("");
    const authError = ref<string | null>(null);

    const startupMessengerSocket = async () => {
      token.value = await getAccessToken();

      const socketURL = configStore.remoteConfig.messages_socket_base;
      // Make sure only one socket is open
      if (!socket.value) {
        socket.value = io(socketURL, {
          transports: ["polling", "websocket"],
          withCredentials: true,
          extraHeaders: {
            authorization: `Bearer ${token.value}`,
          },
        });
        socket.value.connect();
        // When connected successfully, subscribe so that new comments are delivered
        addEventListeners(socket.value);

        // In Case the connection gets lost
        socket.value.io.on("reconnect_error", async (error) => {
          const isOnline = useOnline();
          // Behind this is USUALLY a 401 not Authorized which means we need a new Token
          if (error.message === "xhr poll error" && isOnline.value) {
            token.value = await getAccessToken();
            socket.value?.io.reconnectionAttempts(0);
            disconnectSocket();
            // Socket.IO is not that smart. We need to start a whole new instance, otherwise it will always use the same, expired token
            try {
              socket.value = io(socketURL, {
                transports: ["polling", "websocket"],
                withCredentials: true,
                extraHeaders: {
                  authorization: `Bearer ${token.value}`,
                },
              });
              // New Instance means we need to add all the event listeners agajn
              addEventListeners(socket.value);
            } catch (error) {
              // eslint-disable-next-line
              console.error(error, "error while reconnecting");
            }
          }
        });
      }
    };

    const disconnectSocket = () => {
      socket.value?.removeAllListeners();
      if (isConnected.value) {
        if (!isSocketAuthenticated.value) return;
        socket.value?.emit("topic_message_unsubscribe");
        socket.value?.emit("value_unit_comment_envelope_unsubscribe");
        socket.value?.disconnect();
        isConnected.value = false;
        isTopicSocketDataUpdated.value = false;
        isCommentSocketDataUpdated.value = false;
      }
    };

    /**
     * Topicsuggestion and Comments related
     */

    const addEventListeners = (socket: Socket) => {
      socket.on("connect", () => {
        isConnected.value = true;
      });

      /***
       * handle authentication interface events
       */

      socket.on("authorization-required", async () => {
        try {
          const token = await getAccessToken();
          // the raw token needs to be send here
          socket.emit("authorization", token);
        } catch (err) {
          authError.value = `Failed to get access token: ${(err as Error).message}`;
          console.error(authError.value); // eslint-disable-line no-console
        }
      });

      const handleAuthenticationSuccess = () => {
        /**
         * don't send events to the server, if socket is not authenticated
         */
        if (!isSocketAuthenticated.value) return;
        socket.emit("topic_message_subscribe");
        socket.emit("value_unit_comment_envelope_subscribe");
      };

      socket.on("authorization-success", (message: string) => {
        authError.value = null;
        isSocketAuthenticated.value = true;
        handleAuthenticationSuccess();
        console.log("Authorization successful:", message); // eslint-disable-line no-console
      });

      socket.on("authorization-failed", (reason: string) => {
        isSocketAuthenticated.value = false;
        authError.value = `Authorization failed: ${reason}`;
        console.error(authError.value); // eslint-disable-line no-console
      });

      /**
       * handle messenger service events
       */

      socket.on("value_unit_comment_envelope", (data) => {
        try {
          const parsedData = JSON.parse(data);
          commentsStore.updateEnvelope(parsedData);
          isCommentSocketDataUpdated.value = true;
        } catch (error) {
          // eslint-disable-next-line
          console.error(error, " Could not parse JSON for Comment Envelope.");
        }
      });
      socket.on("topic_message", (rawMessage) => {
        const parsedTopic = JSON.parse(rawMessage);
        topicStore.updateTopics(parsedTopic);
        isTopicSocketDataUpdated.value = true;
      });
      socket.on("topic_message_reply", (rawMessage: string) => {
        const parsedReply = JSON.parse(rawMessage);
        topicStore.updateCurrentTopic(parsedReply);
        isTopicSocketDataUpdated.value = true;
      });
    };

    /**
     * Topicsuggestion  related
     */

    const addTopicSubscription = (topicId: string | string[]) => {
      /**
       * don't send events to the server, if socket is not authenticated
       */
      if (!isSocketAuthenticated.value) return;
      socket.value?.emit("topic_message_replies_subscribe", topicId);
    };

    const removeTopicSubscription = (
      topicId: string | string[] | undefined,
    ) => {
      /**
       * don't send events to the server, if socket is not authenticated
       */
      if (!isSocketAuthenticated.value) return;
      socket.value?.emit("topic_message_replies_unsubscribe", topicId);
    };

    const hasUpdatedData = computed(() => {
      return isTopicSocketDataUpdated.value || isCommentSocketDataUpdated.value;
    });

    /**
     * reset should be set per data type separately
     * in order ensure the green indicator dissapears on click on the corresponding
     * navigation item e.g. enquiry or comment
     */

    function resetUpdatedCommentData() {
      isCommentSocketDataUpdated.value = false;
    }

    function resetUpdatedTopicData() {
      isTopicSocketDataUpdated.value = false;
    }

    return {
      isTopicSocketDataUpdated,
      isCommentSocketDataUpdated,
      isConnected,
      hasUpdatedData,
      startupMessengerSocket,
      disconnectSocket,
      addTopicSubscription,
      removeTopicSubscription,
      resetUpdatedTopicData,
      resetUpdatedCommentData,
    };
  },
);
